import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, vModelText as _vModelText, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "experience-form" }
const _hoisted_2 = {
  key: 0,
  class: "form-title"
}
const _hoisted_3 = {
  key: 1,
  class: "form-title"
}
const _hoisted_4 = { class: "joy-mb-4" }
const _hoisted_5 = ["value"]
const _hoisted_6 = ["value", "checked"]
const _hoisted_7 = { class: "joy-mb-4" }
const _hoisted_8 = { required: "" }
const _hoisted_9 = ["src"]
const _hoisted_10 = { key: 2 }
const _hoisted_11 = ["label"]
const _hoisted_12 = ["selected"]
const _hoisted_13 = ["value", "selected"]
const _hoisted_14 = { required: "" }
const _hoisted_15 = { class: "experience-form__daterange" }
const _hoisted_16 = { class: "date-fields" }
const _hoisted_17 = {
  class: "daterange",
  "data-testid": "experience-form_start-date"
}
const _hoisted_18 = {
  class: "daterange end-date",
  "data-testid": "experience-form_end-date"
}
const _hoisted_19 = { class: "joy-mt-4" }
const _hoisted_20 = ["value", "checked"]
const _hoisted_21 = { class: "experience-form__actions" }
const _hoisted_22 = { class: "right-side-actions" }
const _hoisted_23 = ["disabled"]

import {ActivityAreaResource, AddressResource, MinMaxRuleResource} from '@/common/api/api-raw-types';
    import LocationAutocomplete from '@/common/components/location-autocomplete/LocationAutocomplete.vue';
    import {DI} from '@/profile-creation-wizard/common/DependencyInjection';
    import {Photo} from '@malt/asset-utils';
    import {pushVJoySnackbar} from '@maltjoy/core-vue';
    import {getCalendarProperties, useTranslation} from '@malt/vue3-use';
    import {addDays} from 'date-fns';
    import {computed, inject, ref, watch} from 'vue';
    import {ActivityArea, CompanySuggestion, Experience} from '../../../domain/experience/Experience';

    type ExperienceFormEvents = {
        (e: 'experience-form:cancel'): void;
        (e: 'experience-form:add', experience: Experience): void;
        (e: 'experience-form:edit', experience: Experience): void;
    };

    // This event type should be replaced by the one from primevue...
    type AutoCompleteCompleteEvent = {query: string};

    
export default /*@__PURE__*/_defineComponent({
  __name: 'ExperienceForm',
  props: {
    experienceToEdit: {},
    activityAreas: {},
    descriptionValidationRules: {}
  },
  emits: ["experience-form:cancel", "experience-form:add", "experience-form:edit"],
  setup(__props: any, { emit: __emit }) {

    const experienceDomainService = inject(DI.experienceDomainService);

    const {msg} = useTranslation();

    const props = __props;

    const emits = __emit;

    const {datePattern, datePatternRegex} = getCalendarProperties();

    const isSaveDisabled = ref(!props.experienceToEdit);
    const companySuggestions = ref<CompanySuggestion[]>([]);

    const experience = ref<Experience>(props.experienceToEdit ? {...props.experienceToEdit} : {});
    const selectedCompanySuggestion = ref<CompanySuggestion>(props.experienceToEdit?.company);

    experience.value.description = experience.value.description ?? '';
    experience.value.endDate = experience.value.isCurrentJob === true ? undefined : experience.value.endDate;

    const maxEndDate = new Date();
    const maxStartDate = computed(() => experience.value.endDate || maxEndDate);
    const minEndDate = computed(() => addDays(experience.value.startDate, 1));

    const isEnabledEndDate = computed(() => experience.value.isCurrentJob !== true);
    const isEditMode = computed(() => !!props.experienceToEdit?.id);

    const minLengthLabel = computed(() =>
        msg(
            'profile.creation.completion.wizard.v2.experience.description.minLengthLabel',
            props.descriptionValidationRules.min,
            props.descriptionValidationRules.max,
        ),
    );

    const descriptionMinLength = computed(() => props.descriptionValidationRules.min);
    const descriptionMaxLength = computed(() => props.descriptionValidationRules.max);

    watch(
        () => experience.value,
        async () => {
            isSaveDisabled.value = !experienceDomainService!.isValidExperience(experience.value, props.descriptionValidationRules);
        },
        {deep: true},
    );

    function handleCancel() {
        emits('experience-form:cancel');
        if (props.experienceToEdit) {
            experience.value = {...props.experienceToEdit};
        }
    }

    function handleFormAreaChange(e: CustomEvent) {
        experience.value.area = e.detail.value;
    }

    function handleFormAsFreelancerChange(e: CustomEvent) {
        experience.value.asFreelancer = e.detail;
    }

    function handleFormCompanyComplete(e: AutoCompleteCompleteEvent) {
        searchCompanies(e.query);
    }
    function handleFormCompanyUpdate(value: CompanySuggestion | string) {
        // The AutoComplete returns a string when its updated without selecting a suggestion
        if (typeof value === 'string') {
            experience.value.companyId = '';
            experience.value.company = value;
        } else {
            experience.value.companyId = value.companyId;
            experience.value.company = value.name;

            if (value.others && value.others.areacode) {
                experience.value.area = value.others.areacode as ActivityAreaResource;
            }
        }
    }
    function handleFormDescriptionChange(e: CustomEvent) {
        experience.value.description = e;
    }
    function handleFormEndDateUpdate(value: Date) {
        experience.value.endDate = value;
    }
    function handleFormIsCurrentJobChange(e: CustomEvent) {
        experience.value.isCurrentJob = e.detail;

        if (experience.value.isCurrentJob === true) {
            experience.value.endDate = undefined;
        }
    }
    function handleFormLocationChange(value: AddressResource) {
        experience.value.location = value;
    }
    function handleFormTitleChange(event: CustomEvent) {
        experience.value.title = event.detail.value;
    }
    function handleFormStartDateUpdate(value: Date) {
        experience.value.startDate = value;
    }

    function handleValidate() {
        if (!experience.value.id) {
            emits('experience-form:add', experience.value);
        } else {
            emits('experience-form:edit', experience.value);
        }
    }

    function companyLogoUrl(baseUrl: string, logoName: string): string {
        return new Photo(baseUrl, logoName).getFaceFilledUrl({width: 32, height: 34});
    }

    function getActivityAreaTranslationKey(activityAreaCode: string): string | undefined {
        const result = props.activityAreas.find((activityArea) => activityArea.id === activityAreaCode);
        return result?.translationKey;
    }

    async function searchCompanies(query: string): Promise<void> {
        try {
            companySuggestions.value = await experienceDomainService!.searchCompanies(query);
        } catch (e) {
            displayTechnicalErrorNotification();
        }
    }

    function displayTechnicalErrorNotification() {
        pushVJoySnackbar({
            props: {
                message: msg('profile.creation.common.notification.error.technical'),
                level: 'error',
            },
        });
    }

return (_ctx: any,_cache: any) => {
  const _component_VJoyWrapper = _resolveComponent("VJoyWrapper")!
  const _component_VJoyIcon = _resolveComponent("VJoyIcon")!
  const _component_AutoComplete = _resolveComponent("AutoComplete")!
  const _component_MaltCalendar = _resolveComponent("MaltCalendar")!
  const _component_VJoyTextarea = _resolveComponent("VJoyTextarea")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_VJoyWrapper, {
      direction: "column",
      align: "stretch"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_VJoyWrapper, {
          justify: "space-between",
          "no-margin": true,
          align: "flex-start"
        }, {
          default: _withCtx(() => [
            (isEditMode.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(_unref(msg)('profile.creation.completion.wizard.v2.experience.editExperience.form.title')), 1))
              : (_openBlock(), _createElementBlock("div", _hoisted_3, _toDisplayString(_unref(msg)('profile.creation.completion.wizard.v2.experience.addNewExperience.form.title')), 1))
          ]),
          _: 1
        }),
        _createElementVNode("div", null, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("joy-input", {
              "data-testid": "experience-form_title",
              required: true,
              "required-mark": "",
              value: experience.value.title,
              onJoyInputChange: handleFormTitleChange
            }, _toDisplayString(_unref(msg)('profile.creation.completion.wizard.v2.experience.jobTitle.label')), 41, _hoisted_5)
          ]),
          _createElementVNode("joy-checkbox", {
            "data-testid": "experience-form_as-freelancer",
            value: experience.value.asFreelancer,
            checked: experience.value.asFreelancer,
            onJoyCheckboxChange: handleFormAsFreelancerChange
          }, _toDisplayString(_unref(msg)('profile.creation.completion.wizard.v2.experience.asFreelancer.label')), 41, _hoisted_6)
        ]),
        _createElementVNode("div", null, [
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("joy-label", _hoisted_8, _toDisplayString(_unref(msg)('profile.creation.completion.wizard.v2.experience.company.label')), 1),
            _createVNode(_component_AutoComplete, {
              modelValue: selectedCompanySuggestion.value,
              "onUpdate:modelValue": [
                _cache[0] || (_cache[0] = ($event: any) => ((selectedCompanySuggestion).value = $event)),
                handleFormCompanyUpdate
              ],
              "data-testid": "experience-form_company-suggestion",
              required: true,
              suggestions: companySuggestions.value,
              optionLabel: "name",
              "scroll-height": "auto",
              "auto-option-focus": false,
              onComplete: handleFormCompanyComplete
            }, {
              item: _withCtx((slotProps) => [
                _createVNode(_component_VJoyWrapper, {
                  justify: "flex-start",
                  align: "center",
                  class: "experience-form___autocomplete-item",
                  "no-margin": "true"
                }, {
                  default: _withCtx(() => [
                    (slotProps.item.others.logoName)
                      ? (_openBlock(), _createElementBlock("img", {
                          key: 0,
                          src: companyLogoUrl(slotProps.item.others.logoBaseUrl, slotProps.item.others.logoName)
                        }, null, 8, _hoisted_9))
                      : (_openBlock(), _createBlock(_component_VJoyIcon, {
                          key: 1,
                          name: "company-placeholder",
                          color: "secondary",
                          size: "medium"
                        })),
                    _createElementVNode("span", null, _toDisplayString(slotProps.item.name), 1),
                    (slotProps.item.others.areacode)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_10, _toDisplayString(_unref(msg)(getActivityAreaTranslationKey(slotProps.item.others.areacode))), 1))
                      : _createCommentVNode("", true)
                  ]),
                  _: 2
                }, 1024)
              ]),
              _: 1
            }, 8, ["modelValue", "suggestions"])
          ]),
          _withDirectives(_createElementVNode("joy-select", {
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((experience.value.area) = $event)),
            "data-testid": "experience-form_industry",
            label: _unref(msg)('profile.creation.completion.wizard.v2.experience.industry.label'),
            onJoySelectChange: handleFormAreaChange
          }, [
            _createElementVNode("option", {
              disabled: "",
              selected: !experience.value.area
            }, _toDisplayString(_unref(msg)('profile.creation.completion.wizard.v2.experience.industry.placeholder')), 9, _hoisted_12),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.activityAreas, (activityArea) => {
              return (_openBlock(), _createElementBlock("option", {
                key: `activity-area_${activityArea.id}`,
                value: activityArea.id,
                selected: experience.value.area === activityArea.id
              }, _toDisplayString(_unref(msg)(activityArea.translationKey)), 9, _hoisted_13))
            }), 128))
          ], 40, _hoisted_11), [
            [_vModelText, experience.value.area]
          ])
        ]),
        _createElementVNode("div", null, [
          _createElementVNode("joy-label", _hoisted_14, _toDisplayString(_unref(msg)('profile.creation.completion.wizard.v2.experience.location.label')), 1),
          _createVNode(LocationAutocomplete, {
            "data-testid": "experience-form_location",
            required: true,
            address: experience.value.location,
            "onUpdate:address": handleFormLocationChange
          }, null, 8, ["address"])
        ]),
        _createElementVNode("div", _hoisted_15, [
          _createElementVNode("div", _hoisted_16, [
            _createElementVNode("div", _hoisted_17, [
              _createElementVNode("joy-label", null, _toDisplayString(`${_unref(msg)('profile.creation.completion.wizard.v2.experience.startDate.label')} *`), 1),
              _createVNode(_component_MaltCalendar, {
                modelValue: experience.value.startDate,
                "onUpdate:modelValue": [
                  _cache[2] || (_cache[2] = ($event: any) => ((experience.value.startDate) = $event)),
                  handleFormStartDateUpdate
                ],
                "data-testid": "experience-form_start-date-calendar",
                datePattern: _unref(datePattern),
                datePatternRegex: _unref(datePatternRegex),
                maxDate: maxStartDate.value
              }, null, 8, ["modelValue", "datePattern", "datePatternRegex", "maxDate"])
            ]),
            _createElementVNode("div", _hoisted_18, [
              _createElementVNode("joy-label", null, _toDisplayString(_unref(msg)('profile.creation.completion.wizard.v2.experience.endDate.label')), 1),
              _createVNode(_component_MaltCalendar, {
                "data-testid": "experience-form_end-date-calendar",
                disabled: !isEnabledEndDate.value,
                maxDate: _unref(maxEndDate),
                minDate: minEndDate.value,
                datePattern: _unref(datePattern),
                datePatternRegex: _unref(datePatternRegex),
                modelValue: experience.value.endDate,
                "onUpdate:modelValue": handleFormEndDateUpdate
              }, null, 8, ["disabled", "maxDate", "minDate", "datePattern", "datePatternRegex", "modelValue"])
            ])
          ]),
          _createElementVNode("div", _hoisted_19, [
            _createElementVNode("joy-checkbox", {
              value: experience.value.isCurrentJob,
              "data-testid": "experience-form_current-job",
              checked: experience.value.isCurrentJob,
              onJoyCheckboxChange: handleFormIsCurrentJobChange
            }, _toDisplayString(_unref(msg)('profile.creation.completion.wizard.v2.experience.currentPosition.label')), 41, _hoisted_20)
          ])
        ]),
        _createElementVNode("div", null, [
          (props.descriptionValidationRules)
            ? (_openBlock(), _createBlock(_component_VJoyTextarea, {
                key: 0,
                "data-testid": "experience-form_description",
                class: "experience-form__description",
                label: _unref(msg)('profile.creation.completion.wizard.v2.experience.description.label'),
                placeholder: _unref(msg)('profile.creation.completion.wizard.v2.experience.description.placeholder'),
                autogrow: "",
                "model-value": experience.value.description,
                minlength: descriptionMinLength.value,
                maxlength: descriptionMaxLength.value,
                "minlength-label": minLengthLabel.value,
                "onUpdate:modelValue": handleFormDescriptionChange
              }, null, 8, ["label", "placeholder", "model-value", "minlength", "maxlength", "minlength-label"]))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }),
    _createElementVNode("div", _hoisted_21, [
      _createElementVNode("div", _hoisted_22, [
        _createElementVNode("joy-button", {
          "data-testid": "experience-form_cta-cancel",
          variant: "secondary",
          onClick: handleCancel
        }, _toDisplayString(_unref(msg)('general.cancel')), 1),
        _createElementVNode("joy-button", {
          "data-testid": "experience-form_cta-save",
          variant: "primary",
          disabled: isSaveDisabled.value,
          onClick: handleValidate
        }, _toDisplayString(isEditMode.value ? _unref(msg)('general.save') : _unref(msg)('general.add')), 9, _hoisted_23)
      ])
    ])
  ]))
}
}

})